import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Play, Pause, Volume2, VolumeX, Maximize, Minimize,
  ArrowLeft, Settings, Subtitles, Rewind, FastForward, Users, MonitorPlay
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`;
};

export const VideoPlayerPage = () => {
  const { movieId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const heartbeatRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  const [session, setSession] = useState(null);
  const [movieTitle, setMovieTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showSubtitleMenu, setShowSubtitleMenu] = useState(false);
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  const [activeSubtitle, setActiveSubtitle] = useState('off');
  const [activeResolution, setActiveResolution] = useState(null); // 'res' key from session.resolutions
  const [currentSrc, setCurrentSrc] = useState(null);
  const [showControls, setShowControls] = useState(true);

  const endSession = useCallback(async () => {
    const v = videoRef.current;
    const pos = v ? Math.floor(v.currentTime || 0) : 0;
    try {
      await axios.post(`${API}/movies/${movieId}/watch/end`, { watch_position: pos }, { withCredentials: true });
    } catch (e) { console.error('Failed to end watch session:', e); }
  }, [movieId]);

  useEffect(() => {
    const start = async () => {
      try {
        const { data } = await axios.post(`${API}/movies/${movieId}/watch/start`, {}, { withCredentials: true });
        setSession(data);
        // Default source: prefer the highest available resolution, else the legacy single video_url
        if (data.resolutions && data.resolutions.length > 0) {
          setActiveResolution(data.resolutions[0].res);
          setCurrentSrc(data.resolutions[0].url);
        } else {
          setCurrentSrc(data.video_url);
        }
        heartbeatRef.current = setInterval(() => {
          const v = videoRef.current;
          const pos = v ? Math.floor(v.currentTime || 0) : 0;
          axios.post(`${API}/movies/${movieId}/watch/heartbeat`, { watch_position: pos }, { withCredentials: true }).catch(() => {});
        }, 20000);
      } catch (err) {
        const detail = err.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Failed to start playback');
      } finally {
        setLoading(false);
      }
    };
    start();
    axios.get(`${API}/movies/${movieId}`, { withCredentials: true })
      .then(({ data }) => setMovieTitle(data.title))
      .catch(() => {});
    return () => {
      endSession();
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  }, [movieId, endSession]);

  // Switch resolution: save currentTime + playing state, swap src, restore.
  const changeResolution = (res) => {
    if (!session?.resolutions) return;
    const target = session.resolutions.find(r => r.res === res);
    if (!target || res === activeResolution) { setShowQualityMenu(false); return; }
    const v = videoRef.current;
    const wasPlaying = v ? !v.paused : false;
    const resumeAt = v ? v.currentTime || 0 : 0;
    setActiveResolution(res);
    setCurrentSrc(target.url);
    setShowQualityMenu(false);
    // After React re-renders with new src, restore playhead + play state
    setTimeout(() => {
      const el = videoRef.current;
      if (!el) return;
      const restore = () => {
        try { el.currentTime = resumeAt; } catch (e) { /* ignore */ }
        if (wasPlaying) el.play().catch(() => {});
        el.removeEventListener('loadedmetadata', restore);
      };
      el.addEventListener('loadedmetadata', restore);
      el.load();
    }, 0);
  };

  const handleLoadedMetadata = () => {
    const v = videoRef.current;
    if (v && session?.resume_position && session.resume_position > 5 && session.resume_position < v.duration - 10) {
      v.currentTime = session.resume_position;
      toast.info(`Resumed from ${formatTime(session.resume_position)}`);
    }
    setDuration(v?.duration || 0);
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play(); else v.pause();
  };

  const seek = (delta) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min(v.duration || 0, v.currentTime + delta));
  };

  const handleSeekBar = (e) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    v.currentTime = ratio * duration;
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  };

  const handleVolume = (val) => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = val;
    v.muted = val === 0;
    setVolume(val);
    setIsMuted(val === 0);
  };

  const toggleFullscreen = async () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      try { await el.requestFullscreen?.(); setIsFullscreen(true); } catch (e) { console.warn('Fullscreen enter failed:', e); }
    } else {
      try { await document.exitFullscreen?.(); setIsFullscreen(false); } catch (e) { console.warn('Fullscreen exit failed:', e); }
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const changeSpeed = (s) => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = s;
    setPlaybackSpeed(s);
    setShowSpeedMenu(false);
  };

  const showControlsTemporarily = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  };

  useEffect(() => {
    const handler = (e) => {
      if (['INPUT', 'TEXTAREA'].includes(e.target?.tagName)) return;
      if (e.code === 'Space') { e.preventDefault(); togglePlay(); }
      else if (e.code === 'ArrowRight') seek(10);
      else if (e.code === 'ArrowLeft') seek(-10);
      else if (e.code === 'KeyF') toggleFullscreen();
      else if (e.code === 'KeyM') toggleMute();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#000000' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: '#3B82F6' }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#000000' }}>
        <div className="max-w-md text-center">
          <p className="text-xl font-bold mb-2" style={{ color: '#EF4444' }}>Unable to start playback</p>
          <p className="mb-6" style={{ color: '#DDDDDD' }}>{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-5 py-3 rounded-lg font-semibold"
            style={{ backgroundColor: '#1E3A8A', color: 'white' }}
            data-testid="watch-error-back"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const subtitles = session?.subtitles || [];

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: '#000000' }}
      onMouseMove={showControlsTemporarily}
      onClick={showControlsTemporarily}
      data-testid="watch-page"
    >
      <video
        ref={videoRef}
        src={currentSrc || session?.video_url}
        className="w-full h-full object-contain"
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onClick={togglePlay}
        data-testid="video-element"
      >
        {subtitles.map((s) => (
          <track
            key={s.lang}
            kind="subtitles"
            src={s.url}
            srcLang={s.lang}
            label={s.label || s.lang}
          />
        ))}
      </video>

      <div
        className="absolute top-0 left-0 right-0 p-6 flex items-start justify-between gap-6 transition-opacity duration-300"
        style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.85) 0%, transparent 100%)',
          opacity: showControls ? 1 : 0,
          pointerEvents: showControls ? 'auto' : 'none'
        }}
      >
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-white/10 flex-shrink-0"
            style={{ color: '#FFFFFF' }}
            data-testid="watch-back-button"
          >
            <ArrowLeft size={20} /> Back
          </button>
          <h1
            className="text-2xl sm:text-3xl md:text-4xl font-bold truncate"
            style={{ color: '#FFFFFF', textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}
            data-testid="player-title"
            title={movieTitle}
          >
            {movieTitle}
          </h1>
        </div>
        {session && (
          <div className="flex-shrink-0 px-3 py-1.5 rounded-full flex items-center gap-1.5 text-sm"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)', color: '#DDDDDD', border: '1px solid rgba(255,255,255,0.15)' }}>
            <Users size={14} /> {session.current_viewers}/{session.max_viewers}
          </div>
        )}
      </div>

      {!isPlaying && (
        <button
          onClick={togglePlay}
          className="absolute p-6 rounded-full transition-transform hover:scale-110"
          style={{ backgroundColor: 'rgba(30, 58, 138, 0.85)' }}
          data-testid="center-play-button"
          aria-label="Play"
        >
          <Play size={48} color="white" fill="white" />
        </button>
      )}

      <div
        className="absolute bottom-0 left-0 right-0 p-6 transition-opacity duration-300"
        style={{
          background: 'linear-gradient(0deg, rgba(0,0,0,0.9) 0%, transparent 100%)',
          opacity: showControls ? 1 : 0,
          pointerEvents: showControls ? 'auto' : 'none'
        }}
      >
        <div
          className="w-full h-1.5 rounded-full cursor-pointer mb-4 group"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.25)' }}
          onClick={handleSeekBar}
          data-testid="progress-bar"
        >
          <div
            className="h-full rounded-full relative"
            style={{
              width: `${duration ? (currentTime / duration) * 100 : 0}%`,
              backgroundColor: '#3B82F6'
            }}
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full opacity-0 group-hover:opacity-100"
              style={{ backgroundColor: '#3B82F6' }} />
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => seek(-10)} className="p-2 hover:bg-white/10 rounded" data-testid="rewind-button" title="Rewind 10s">
              <Rewind size={22} color="white" />
            </button>
            <button onClick={togglePlay} className="p-2 hover:bg-white/10 rounded" data-testid="play-pause-button">
              {isPlaying ? <Pause size={26} color="white" fill="white" /> : <Play size={26} color="white" fill="white" />}
            </button>
            <button onClick={() => seek(10)} className="p-2 hover:bg-white/10 rounded" data-testid="forward-button" title="Forward 10s">
              <FastForward size={22} color="white" />
            </button>

            <div className="flex items-center gap-2 ml-2">
              <button onClick={toggleMute} className="p-2 hover:bg-white/10 rounded" data-testid="mute-button">
                {isMuted || volume === 0 ? <VolumeX size={22} color="white" /> : <Volume2 size={22} color="white" />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={isMuted ? 0 : volume}
                onChange={(e) => handleVolume(parseFloat(e.target.value))}
                className="w-24"
                style={{ accentColor: '#3B82F6' }}
                data-testid="volume-slider"
              />
            </div>

            <span className="text-sm font-mono ml-2" style={{ color: '#FFFFFF' }}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          <div className="flex items-center gap-2 relative">
            <div className="relative">
              <button
                onClick={() => { setShowSubtitleMenu(v => !v); setShowSpeedMenu(false); setShowQualityMenu(false); }}
                className="p-2 hover:bg-white/10 rounded flex items-center gap-1"
                style={{ color: activeSubtitle !== 'off' ? '#3B82F6' : '#FFFFFF' }}
                data-testid="subtitles-button"
                title="Subtitles"
              >
                <Subtitles size={22} />
              </button>
              {showSubtitleMenu && (
                <div className="absolute bottom-12 right-0 rounded-lg overflow-hidden min-w-[160px]"
                  style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
                  <div className="px-3 py-2 text-xs uppercase font-bold" style={{ color: '#8197A4' }}>Subtitles</div>
                  <button
                    onClick={() => {
                      setActiveSubtitle('off');
                      const v = videoRef.current;
                      if (v) Array.from(v.textTracks).forEach(t => { t.mode = 'hidden'; });
                      setShowSubtitleMenu(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-white/10"
                    style={{ color: activeSubtitle === 'off' ? '#3B82F6' : '#FFFFFF' }}
                  >
                    Off
                  </button>
                  {subtitles.length === 0 && (
                    <div className="px-4 py-3 text-xs" style={{ color: '#8197A4' }}>No subtitles available</div>
                  )}
                  {subtitles.map(s => (
                    <button
                      key={s.lang}
                      onClick={() => {
                        setActiveSubtitle(s.lang);
                        const v = videoRef.current;
                        if (v) {
                          Array.from(v.textTracks).forEach(t => {
                            t.mode = t.language === s.lang ? 'showing' : 'hidden';
                          });
                        }
                        setShowSubtitleMenu(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-white/10"
                      style={{ color: activeSubtitle === s.lang ? '#3B82F6' : '#FFFFFF' }}
                    >
                      {s.label || s.lang}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => { setShowSpeedMenu(v => !v); setShowSubtitleMenu(false); setShowQualityMenu(false); }}
                className="p-2 hover:bg-white/10 rounded flex items-center gap-1"
                style={{ color: '#FFFFFF' }}
                data-testid="speed-button"
                title="Playback speed"
              >
                <Settings size={22} />
                <span className="text-xs font-mono">{playbackSpeed}x</span>
              </button>
              {showSpeedMenu && (
                <div className="absolute bottom-12 right-0 rounded-lg overflow-hidden min-w-[120px]"
                  style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
                  <div className="px-3 py-2 text-xs uppercase font-bold" style={{ color: '#8197A4' }}>Speed</div>
                  {PLAYBACK_SPEEDS.map(s => (
                    <button
                      key={s}
                      onClick={() => changeSpeed(s)}
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-white/10"
                      style={{ color: playbackSpeed === s ? '#3B82F6' : '#FFFFFF' }}
                      data-testid={`speed-option-${s}`}
                    >
                      {s}x {s === 1 && '(Normal)'}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Quality (multi-resolution) selector — only shows when 2+ resolutions available */}
            {session?.resolutions?.length > 1 && (
              <div className="relative">
                <button
                  onClick={() => { setShowQualityMenu(v => !v); setShowSpeedMenu(false); setShowSubtitleMenu(false); }}
                  className="p-2 hover:bg-white/10 rounded flex items-center gap-1"
                  style={{ color: '#FFFFFF' }}
                  data-testid="quality-button"
                  title="Video quality"
                >
                  <MonitorPlay size={22} />
                  <span className="text-xs font-mono">
                    {(session.resolutions.find(r => r.res === activeResolution)?.label) || 'HD'}
                  </span>
                </button>
                {showQualityMenu && (
                  <div className="absolute bottom-12 right-0 rounded-lg overflow-hidden min-w-[140px]"
                    style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }} data-testid="quality-menu">
                    <div className="px-3 py-2 text-xs uppercase font-bold" style={{ color: '#8197A4' }}>Quality</div>
                    {session.resolutions.map(r => (
                      <button
                        key={r.res}
                        onClick={() => changeResolution(r.res)}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-white/10"
                        style={{ color: activeResolution === r.res ? '#3B82F6' : '#FFFFFF' }}
                        data-testid={`quality-option-${r.res}`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button onClick={toggleFullscreen} className="p-2 hover:bg-white/10 rounded" data-testid="fullscreen-button" title="Fullscreen (F)">
              {isFullscreen ? <Minimize size={22} color="white" /> : <Maximize size={22} color="white" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
